/**
 * One /overview box: a card with its title, and the five states every box
 * shares (spec §5). A failed read is an alert, never 0; nothing in the window
 * is one line, never zeros; a workspace-bound box in the all-workspaces view
 * asks for a workspace. A box given `onRetry` (the Channel Deep-dive, D2 R10)
 * offers "ลองอีกครั้ง" under its failure. D1 passes none, so D1 is unchanged.
 *
 * The five states are BoxBody, exported (D2 P11): the card draws it under its
 * title, and the Channel Deep-dive's breakdown card draws it inside each tab,
 * so a tab and a box cannot drift apart.
 *
 * @module components/overview/OverviewBox
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BoxError, BoxMessage } from '@/components/looker/app/appBoxShared';
import type { BoxState } from '@/services/overview/overviewTypes';

const RETRY_CLASS =
  'rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-sm font-medium text-ink-900 hover:bg-neutral-50 '
  + 'focus:outline-none focus:ring-2 focus:ring-ink-950';

export const OverviewCard: React.FC<{ id: string; title: string; className?: string; children: React.ReactNode }> = ({ id, title, className = '', children }) => (
  <section data-overview-box={id} className={`min-w-0 break-inside-avoid rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm ${className}`}>
    <h3 className="mb-3 text-sm font-semibold text-ink-950">{title}</h3>
    {children}
  </section>
);

interface BodyProps<T> {
  state: BoxState<T>;
  children: (data: T) => React.ReactNode;
  /** D2 R10: read again on demand; offered only while the box is failed. */
  onRetry?: () => void;
}

/** What a box shows for its state, without the card around it: loading, failed (+ retry), empty, needsWorkspace, or the data. */
export const BoxBody = <T,>({ state, onRetry, children }: BodyProps<T>): React.ReactElement => {
  const { t } = useTranslation();
  return (
    <>
      {state.status === 'loading' && (
        <p role="status" className="flex h-24 animate-pulse items-center justify-center rounded-xl bg-neutral-50 text-sm text-neutral-700">
          {t('looker.app.loading')}
        </p>
      )}
      {state.status === 'failed' && (
        <>
          <BoxError text={t('looker.app.failed')} />
          {onRetry && (
            <div className="-mt-3 pb-3 text-center print:hidden">
              <button type="button" className={RETRY_CLASS} onClick={onRetry}>{t('channels.retry')}</button>
            </div>
          )}
        </>
      )}
      {state.status === 'empty' && <BoxMessage text={t('looker.tileEmpty')} />}
      {state.status === 'needsWorkspace' && <BoxMessage text={t('overview.needsWorkspace')} />}
      {state.status === 'ready' && children(state.data)}
    </>
  );
};

interface Props<T> extends BodyProps<T> {
  id: string;
  title: string;
  className?: string;
}

export const OverviewBox = <T,>({ id, title, state, children, className, onRetry }: Props<T>): React.ReactElement => (
  <OverviewCard id={id} title={title} className={className}>
    <BoxBody state={state} onRetry={onRetry}>{children}</BoxBody>
  </OverviewCard>
);
